/**
 * PersonaReadinessBadgeCompact Component
 * 
 * COMPACT version of PersonaReadinessBadge designed to fit inline in the Active AI Persona bar
 * Displays minimal, space-efficient readiness indicators
 */

import React from 'react';
import { PersonaReadiness } from '../services/storyAnalysisService';

interface PersonaReadinessBadgeCompactProps {
  personaType: 'quiz_critter' | 'role_ranger';
  readiness: PersonaReadiness | null;
  isAnalyzing: boolean;
}

const PersonaReadinessBadgeCompact: React.FC<PersonaReadinessBadgeCompactProps> = ({
  personaType,
  readiness,
  isAnalyzing
}) => {
  // Persona configuration (minimal)
  const config = personaType === 'quiz_critter' 
    ? { icon: '🎮', color: '#2196F3', name: 'Quiz' }
    : { icon: '🎭', color: '#9C27B0', name: 'Role' };

  // Compact styles
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: readiness?.ready ? `${config.color}15` : '#f5f5f5',
    border: `1px solid ${readiness?.ready ? config.color : '#ddd'}`,
    opacity: isAnalyzing ? 0.6 : 1,
    transition: 'all 0.3s ease'
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '12px',
    lineHeight: 1
  };

  const countStyle: React.CSSProperties = {
    color: readiness?.ready ? config.color : '#999',
    fontWeight: 600,
    fontSize: '11px'
  };

  // Analyzing state
  if (isAnalyzing) {
    return (
      <div style={badgeStyle} title={`Analyzing ${config.name}...`}>
        <span style={iconStyle}>⏳</span>
        <span style={countStyle}>...</span>
      </div>
    );
  }

  // Not ready state
  if (!readiness || !readiness.ready) {
    return (
      <div style={badgeStyle} title={`${config.name} not available`}>
        <span style={{...iconStyle, opacity: 0.4}}>{config.icon}</span>
        <span style={{...countStyle, opacity: 0.4}}>0</span>
      </div>
    );
  }

  // Ready state - show count
  return (
    <div 
      style={badgeStyle} 
      title={`${config.name}: ${readiness.opportunities_count} ready`}
    >
      <span style={iconStyle}>{config.icon}</span>
      <span style={countStyle}>{readiness.opportunities_count}</span>
    </div>
  );
};

export default PersonaReadinessBadgeCompact;
